import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import {
  MONEY_PAGE_CSS, MONEY, fmtGBP, fmtGBPShort, RENT_STATUS, RENT_FILTERS,
} from './moneyStyles';

const PAGE_SIZE = 12;

interface RentRow {
  id: number;
  address: string;
  postcode: string;
  property_type: string;
  monthly_rent: number;
  tenant_name: string | null;
  rent_paid: boolean | null;
  rent_due: number | null;
  rent_record_id: number | null;
  tenancy_id: number | null;
  tenancy_start: string | null;
  tenancy_end: string | null;
  room_number: string | null;
  amount_paid: number | null;
  amount_due: number | null;
  occupancy_active?: boolean;
  payment_day?: number;
  tenant_id?: number;
}

type StatusKey = keyof typeof RENT_STATUS;

export default function RentTracker() {
  const { token } = useAuth();
  const [properties, setProperties] = useState<RentRow[]>([]);
  const [overdue, setOverdue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const [chasing, setChasing] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [payingKey, setPayingKey] = useState<string | null>(null);
  const [payAmounts, setPayAmounts] = useState<Record<string, string>>({});
  const [bulkMarking, setBulkMarking] = useState(false);
  const [showOverdue, setShowOverdue] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const [dash, ov] = await Promise.all([
      fetch(`${API_URL}/landlord/dashboard?month=${selectedMonth}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/landlord/rent/overdue`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]);
    setProperties((dash.properties || []).map((p: any) => ({
      ...p,
      monthly_rent: Number(p.monthly_rent) || 0,
      rent_due: p.rent_due != null ? Number(p.rent_due) : null,
      amount_due: p.amount_due != null ? Number(p.amount_due) : (p.rent_due != null ? Number(p.rent_due) : null),
      amount_paid: p.amount_paid != null ? Number(p.amount_paid) : null,
    })));
    setOverdue(ov.overdue || []);
    setLoading(false);
  }, [token, selectedMonth]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [searchTerm, filterTab, selectedMonth]);

  const rKey = (p: RentRow) => p.tenancy_id != null ? `${p.id}-${p.tenancy_id}` : `${p.id}-void`;
  const today = new Date().toISOString().slice(0, 7);
  const isCurrentMonth = selectedMonth === today;
  const isFutureMonth = selectedMonth > today;

  const hasActiveLease = (p: RentRow) => {
    if (!p.tenancy_start || !p.tenancy_end) return false;
    const sel = selectedMonth + '-01';
    return sel >= p.tenancy_start.slice(0, 10) && sel <= p.tenancy_end.slice(0, 10);
  };
  const isOccupied = (p: RentRow) =>
    typeof p.occupancy_active === 'boolean' ? p.occupancy_active : (!!p.tenant_name || hasActiveLease(p));
  const isPartial = (p: RentRow) => !p.rent_paid && (p.amount_paid != null && p.amount_paid > 0);

  const getStatus = (p: RentRow): StatusKey => {
    if (!isOccupied(p)) return 'void';
    if (p.rent_paid) return 'paid';
    if (isPartial(p)) return 'partial';
    if (isFutureMonth) return 'upcoming';
    return 'unpaid';
  };

  const totalDue = properties.filter(isOccupied).reduce((s, p) => s + (p.amount_due || p.monthly_rent || 0), 0);
  const totalReceived = properties.filter(p => p.rent_paid || isPartial(p)).reduce((s, p) => {
    if (p.rent_paid) return s + (p.amount_due || p.monthly_rent || 0);
    return s + (p.amount_paid || 0);
  }, 0);
  const outstanding = Math.max(0, totalDue - totalReceived);
  const counts = {
    paid: properties.filter(p => p.rent_paid === true).length,
    partial: properties.filter(isPartial).length,
    unpaid: properties.filter(p => isOccupied(p) && !p.rent_paid && !isPartial(p)).length,
    void: properties.filter(p => !isOccupied(p)).length,
  };
  const actionCount = isCurrentMonth ? counts.unpaid + counts.partial : 0;

  const filtered = properties.filter(p => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || p.address.toLowerCase().includes(s) || (p.tenant_name || '').toLowerCase().includes(s);
    if (!matchSearch) return false;
    const st = getStatus(p);
    if (filterTab === 'Paid') return st === 'paid';
    if (filterTab === 'Partial') return st === 'partial';
    if (filterTab === 'Unpaid') return st === 'unpaid' || st === 'upcoming';
    if (filterTab === 'Void') return st === 'void';
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterCount = (f: string) => {
    if (f === 'Unpaid') return counts.unpaid;
    if (f === 'Partial') return counts.partial;
    if (f === 'Paid') return counts.paid;
    if (f === 'Void') return counts.void;
    return 0;
  };

  const shiftMonth = (delta: number) => {
    const d = new Date(selectedMonth + '-01');
    d.setMonth(d.getMonth() + delta);
    setSelectedMonth(d.toISOString().slice(0, 7));
  };

  const openPayment = (p: RentRow) => {
    const key = rKey(p);
    const rem = (p.amount_due || p.monthly_rent || 0) - (p.amount_paid || 0);
    setPayAmounts(prev => ({ ...prev, [key]: String(rem > 0 ? rem : (p.amount_due || p.monthly_rent || 0)) }));
    setPayingKey(key);
  };

  const submitPayment = async (prop: RentRow) => {
    const key = rKey(prop);
    const amount = parseFloat(payAmounts[key] || '0');
    if (isNaN(amount) || amount <= 0) { setPayingKey(null); return; }
    setMarking(key); setPayingKey(null);
    try {
      if (prop.rent_record_id) {
        const currentPaid = Number(prop.amount_paid || 0);
        const amountDue = Number(prop.amount_due || prop.monthly_rent || 0);
        const targetPaid = Math.min(amountDue, currentPaid + amount);
        const r = await fetch(`${API_URL}/landlord/rent/${prop.rent_record_id}/partial`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ amount_paid: targetPaid, payment_date: new Date().toISOString().split('T')[0] }),
        });
        if (r.ok) {
          const d = await r.json();
          const rec = d.record;
          setProperties(prev => prev.map(p => rKey(p) === key
            ? { ...p, rent_paid: rec.paid, amount_paid: Number(rec.amount_paid), amount_due: Number(rec.amount_due) } : p));
        }
      } else {
        const r = await fetch(`${API_URL}/landlord/rent/mark_paid`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ property_address: prop.address, month: selectedMonth, tenant_name: prop.tenant_name, amount_paid: amount }),
        });
        if (r.ok) {
          const d = await r.json();
          const rec = d.record;
          setProperties(prev => prev.map(p => rKey(p) === key
            ? { ...p, rent_paid: rec.paid, amount_paid: Number(rec.amount_paid || amount), amount_due: Number(rec.amount_due || prop.monthly_rent), rent_record_id: rec.id } : p));
        }
      }
    } finally { setMarking(null); }
  };

  const bulkMarkPaid = async () => {
    const unpaid = filtered.filter(p => { const st = getStatus(p); return st === 'unpaid' || st === 'partial'; });
    if (!unpaid.length) return;
    setBulkMarking(true);
    try {
      const batchSize = 5;
      for (let i = 0; i < unpaid.length; i += batchSize) {
        await Promise.all(unpaid.slice(i, i + batchSize).map(async prop => {
          const amount = (prop.amount_due || prop.monthly_rent || 0) - (prop.amount_paid || 0);
          if (amount <= 0) return;
          if (prop.rent_record_id) {
            await fetch(`${API_URL}/landlord/rent/${prop.rent_record_id}/partial`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ amount_paid: prop.amount_due || prop.monthly_rent }),
            });
          } else {
            await fetch(`${API_URL}/landlord/rent/mark_paid`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ property_address: prop.address, month: selectedMonth, tenant_name: prop.tenant_name, amount_paid: amount }),
            });
          }
        }));
      }
      showToast(`✅ ${unpaid.length} payment${unpaid.length === 1 ? '' : 's'} recorded`);
      await load();
    } finally { setBulkMarking(false); }
  };

  const chaseOverdue = async (o: any) => {
    const key = `${o.property_address}-${o.month}`;
    setChasing(key);
    try {
      const r = await fetch(`${API_URL}/landlord/rent/chase-overdue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ property_address: o.property_address, month: o.month, tenant_name: o.tenant_name }),
      });
      const d = await r.json();
      showToast(r.ok ? `📧 Chase sent to ${d.sent_to}` : d.error || 'Failed to send');
    } finally { setChasing(null); }
  };

  const sendStatement = async (p: RentRow) => {
    setSending(rKey(p));
    const r = await fetch(`${API_URL}/landlord/rent/statement`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ property_address: p.address, month: selectedMonth }),
    });
    const d = await r.json();
    showToast(r.ok ? `✅ Receipt sent to ${p.tenant_name}` : d.error || 'Failed to send');
    setSending(null);
  };

  const monthLabel = (m: string) => new Date(m + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const collectionPct = totalDue > 0 ? Math.round((totalReceived / totalDue) * 100) : 0;

  return (
    <div className="money-page">
      <style>{MONEY_PAGE_CSS}</style>

      <div className="money-banner-wrap">
        <div className="money-banner-glass">
          <div>
            <h1><span className="money-icon">£</span>Rent Tracker</h1>
            <p>{monthLabel(selectedMonth)} · {properties.length} properties · {collectionPct}% collected</p>
          </div>
          <div className="money-banner-actions">
            <button type="button" className="money-btn-ghost" onClick={() => shiftMonth(-1)}>←</button>
            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="money-input-dark" />
            <button type="button" className="money-btn-ghost" onClick={() => shiftMonth(1)}>→</button>
            <button type="button" className="money-btn-ghost" onClick={load}>Refresh</button>
            {isCurrentMonth && actionCount > 0 && (
              <button type="button" className="money-btn-gold" onClick={bulkMarkPaid} disabled={bulkMarking}>
                {bulkMarking ? '…' : `Mark all paid (${actionCount})`}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="money-kpi-row">
        <div className="money-kpi money-kpi-black">
          <div className="kl">Expected</div>
          <div className="kv">{fmtGBPShort(totalDue)}</div>
          <div className="ks">This month</div>
        </div>
        <div className="money-kpi money-kpi-green">
          <div className="kl">Received</div>
          <div className="kv">{fmtGBPShort(totalReceived)}</div>
          <div className="ks">{counts.paid} paid{counts.partial > 0 ? ` · ${counts.partial} partial` : ''}</div>
        </div>
        <div className="money-kpi money-kpi-red">
          <div className="kl">Outstanding</div>
          <div className="kv">{outstanding > 0 ? fmtGBPShort(outstanding) : '—'}</div>
          <div className="ks">{counts.unpaid} unpaid</div>
        </div>
        <div className="money-kpi money-kpi-white">
          <div className="kl">Vacant</div>
          <div className="kv">{counts.void}</div>
          <div className="ks">void / to let</div>
        </div>
      </div>

      {isCurrentMonth && actionCount > 0 && (
        <div className="money-alert-slim red">
          <span>🔴 {counts.unpaid} unpaid · {counts.partial} partial this month</span>
          <button type="button" className="money-btn-sm" style={{ background: MONEY.red, color: '#fff' }}
            onClick={() => setFilterTab('Unpaid')}>Show unpaid</button>
        </div>
      )}

      {overdue.length > 0 && (
        <div className="money-alert-slim amber">
          <span>⚠️ {overdue.length} overdue from prior months</span>
          <button type="button" className="money-btn-sm" style={{ background: MONEY.black, color: '#fff' }}
            onClick={() => setShowOverdue(v => !v)}>{showOverdue ? 'Hide' : 'View'}</button>
        </div>
      )}

      {showOverdue && overdue.length > 0 && (
        <div className="money-panel" style={{ marginBottom: 16, backgroundImage: 'none' }}>
          <div className="money-panel-title">Overdue rent</div>
          <table className="money-table">
            <thead><tr><th>Property</th><th>Tenant</th><th>Amount</th><th>Months</th><th></th></tr></thead>
            <tbody>
              {overdue.map((o: any, i: number) => (
                <tr key={i}>
                  <td style={{ fontWeight: 700 }}>{o.property_address}</td>
                  <td>{o.tenant_name}</td>
                  <td style={{ fontFamily: MONEY.mono, fontWeight: 700 }}>{fmtGBP(Number(o.amount_due))}</td>
                  <td><span className="money-badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>{o.months_overdue}mo</span></td>
                  <td>
                    <button type="button" className="money-btn-sm" style={{ background: MONEY.red, color: '#fff' }}
                      onClick={() => chaseOverdue(o)} disabled={chasing === `${o.property_address}-${o.month}`}>
                      {chasing === `${o.property_address}-${o.month}` ? '…' : '📧 Chase'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="money-toolbar">
        <div className="money-search">
          <span style={{ color: '#a8a29e' }}>🔍</span>
          <input placeholder="Search address or tenant…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select className="money-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
          {RENT_FILTERS.map(f => (
            <option key={f} value={f}>{f}{f !== 'All' && filterCount(f) > 0 ? ` (${filterCount(f)})` : ''}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, fontWeight: 700, color: MONEY.muted, marginLeft: 'auto' }}>{filtered.length} shown</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: MONEY.muted }}>Loading…</div>
      ) : properties.length === 0 ? (
        <div className="money-empty">
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏘️</div>
          <div style={{ fontWeight: 800, color: MONEY.black }}>No properties yet</div>
          <div style={{ color: MONEY.muted, fontSize: 13, marginTop: 6 }}>Add a property to start tracking rent</div>
          <a href="/landlord/properties/new" style={{
            display: 'inline-block', marginTop: 16, padding: '10px 22px',
            background: '#2563eb', color: '#fff', borderRadius: 10,
            fontWeight: 800, fontSize: 13, textDecoration: 'none',
          }}>+ Add property</a>
        </div>
      ) : filtered.length === 0 ? (
        <div className="money-empty">
          <div style={{ fontSize: 36, marginBottom: 10 }}>£</div>
          <div style={{ fontWeight: 800, color: MONEY.black }}>No properties match</div>
          <div style={{ color: MONEY.muted, fontSize: 13, marginTop: 6 }}>Try a different filter or month</div>
        </div>
      ) : (
        <>
          <div className="money-grid">
            {pageItems.map(p => {
              const key = rKey(p);
              const status = getStatus(p);
              const st = RENT_STATUS[status];
              const fullAmt = p.amount_due || p.monthly_rent || 0;
              const paidAmt = p.amount_paid || 0;
              const pct = fullAmt > 0 ? Math.min(100, Math.round((paidAmt / fullAmt) * 100)) : 0;
              const isPaying = payingKey === key;
              const isMarkingThis = marking === key;

              return (
                <div key={key} className="money-card">
                  <div className="money-card-stripe" style={{ background: st.stripe }} />
                  <div className="money-card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="money-card-addr">{p.address}</div>
                        <div className="money-card-meta">
                          {p.postcode}{p.room_number ? ` · Room ${p.room_number}` : ''}
                        </div>
                      </div>
                      <span className="money-badge" style={{ background: st.bg, color: st.text }}>{st.label}</span>
                    </div>
                    <div className="money-card-amt">{fmtGBP(fullAmt)}</div>
                    {p.tenant_name ? (
                      <div className="money-card-meta" style={{ fontWeight: 600, color: MONEY.black }}>{p.tenant_name}</div>
                    ) : (
                      <div className="money-card-meta" style={{ fontStyle: 'italic' }}>Vacant</div>
                    )}
                    {status === 'partial' && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: MONEY.muted, marginBottom: 4 }}>
                          <span style={{ color: MONEY.green }}>{fmtGBP(paidAmt)} in</span>
                          <span style={{ color: MONEY.red }}>{fmtGBP(fullAmt - paidAmt)} owed</span>
                        </div>
                        <div style={{ height: 5, background: '#f5f5f4', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: MONEY.amber, borderRadius: 3 }} />
                        </div>
                      </div>
                    )}
                    {p.payment_day && status !== 'void' && (
                      <div className="money-card-meta" style={{ marginTop: 6 }}>Due {p.payment_day}th of month</div>
                    )}
                  </div>
                  <div className="money-card-foot">
                    {isPaying ? (
                      <div className="money-pay-row" style={{ width: '100%' }}>
                        <input type="number" min="0" step="0.01" className="money-pay-input"
                          value={payAmounts[key] || ''}
                          onChange={e => setPayAmounts(prev => ({ ...prev, [key]: e.target.value }))}
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') submitPayment(p); if (e.key === 'Escape') setPayingKey(null); }}
                        />
                        <button type="button" className="money-btn-sm" style={{ background: MONEY.green, color: '#fff' }}
                          onClick={() => submitPayment(p)} disabled={isMarkingThis}>{isMarkingThis ? '…' : '✓'}</button>
                        <button type="button" className="money-btn-sm" style={{ background: '#f5f5f4', color: MONEY.muted }}
                          onClick={() => setPayingKey(null)}>✕</button>
                      </div>
                    ) : status === 'unpaid' ? (
                      <button type="button" className="money-btn-sm" style={{ flex: 1, background: MONEY.red, color: '#fff', padding: '10px' }}
                        onClick={() => openPayment(p)} disabled={isMarkingThis}>
                        {isMarkingThis ? '…' : 'Record payment'}
                      </button>
                    ) : status === 'partial' ? (
                      <button type="button" className="money-btn-sm" style={{ flex: 1, background: MONEY.amber, color: '#fff', padding: '10px' }}
                        onClick={() => openPayment(p)} disabled={isMarkingThis}>
                        {isMarkingThis ? '…' : 'Record balance'}
                      </button>
                    ) : status === 'paid' ? (
                      <button type="button" className="money-btn-sm" style={{ flex: 1, background: MONEY.cream, color: MONEY.black, border: `1.5px solid ${MONEY.black}`, padding: '10px' }}
                        onClick={() => sendStatement(p)} disabled={sending === key}>
                        {sending === key ? 'Sending…' : '📧 Receipt'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: MONEY.muted }}>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="money-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
